import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import {
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
        primary: 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-400',
        secondary: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
        success: 'bg-success-50 text-success-600 dark:bg-success-500/20 dark:text-success-400',
        warning: 'bg-warning-50 text-warning-600 dark:bg-warning-500/20 dark:text-warning-400',
        error: 'bg-error-50 text-error-600 dark:bg-error-500/20 dark:text-error-400',
        danger: 'bg-error-50 text-error-600 dark:bg-error-500/20 dark:text-error-400',
        info: 'bg-blue-50 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400',
        outline: 'border border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300',
      },
      size: {
        sm: 'px-2 py-0.5 text-[10px]',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
  removable?: boolean;
  onRemove?: () => void;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, icon, removable, onRemove, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        {children}
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-0.5 -mr-0.5 p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  }
);
Badge.displayName = 'Badge';

// Status Badge with icon
export interface StatusBadgeProps extends Omit<BadgeProps, 'variant' | 'icon'> {
  status: 'success' | 'warning' | 'error' | 'info' | 'default';
}

const statusConfig = {
  success: { variant: 'success' as const, icon: CheckCircle },
  warning: { variant: 'warning' as const, icon: AlertTriangle },
  error: { variant: 'error' as const, icon: AlertCircle },
  info: { variant: 'primary' as const, icon: Info },
  default: { variant: 'default' as const, icon: null },
};

const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ status, children, ...props }, ref) => {
    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <Badge
        ref={ref}
        variant={config.variant}
        icon={Icon && <Icon className="h-3 w-3" />}
        {...props}
      >
        {children}
      </Badge>
    );
  }
);
StatusBadge.displayName = 'StatusBadge';

export { Badge, StatusBadge, badgeVariants };
